import { BaseComponentImpl } from '../../../../types/implementations';
import { Building } from '../../../../types/protocols';
import { ComponentLifetime } from '../../TemplatesEditor/types';
import { RELEVANT_BASE_TYPES } from '../../../../Helpers/baseTypeUtils';
import { wellFormedComponents } from '../../../../Helpers/componentIntegrity';
import { isConditionSet } from '../../../../Helpers/conditionUtils';
import type { GroupedComponents, ComponentWithReplacement } from '../types';
import * as calculations from './calculations';

/**
 * Process building components and group them by base type
 * Calculates replacement dates and years remaining for each component
 * 
 * @param building - Building data containing components
 * @param componentLifetimes - Lifetime configuration for each component type
 * @returns Grouped components by base type with calculated metadata
 */
export const processComponents = (
    building: Building | null,
    componentLifetimes: Record<string, ComponentLifetime>
): GroupedComponents => {
    if (!building?.model?.structure?.components) {
        return {};
    }

    const grouped: GroupedComponents = {};

    // Define base types in the same order as templates page
    const baseTypeOrder = RELEVANT_BASE_TYPES;

    // Initialize groups for all BaseTypes
    baseTypeOrder.forEach(baseType => {
        grouped[baseType] = [];
    });

    wellFormedComponents(building.model.structure.components).forEach(componentData => {
        // Skip archived components
        if (componentData.archivedAt) return;

        if (!componentData.specificType?.value) return;

        // Convert plain object to BaseComponentImpl instance
        const component = new BaseComponentImpl(componentData);

        const componentType = component.specificType?.value;
        if (!componentType) return;

        const lifetime = componentLifetimes[componentType] || null;
        const hasCondition = isConditionSet(component.condition?.value?.value);
        const hasLifetime = lifetime?.expectedLifetimeYears !== undefined && 
                          lifetime.expectedLifetimeYears > 0;

        let replacementDate: Date | null = null;
        let yearsRemaining: number | null = null;

        if (hasCondition && hasLifetime) {
            replacementDate = calculations.calculateReplacementDate(component, lifetime!, building);
            yearsRemaining = calculations.calculateYearsRemaining(component, lifetime!, building);
        }

        const componentPlan: ComponentWithReplacement = {
            component,
            lifetime,
            replacementDate,
            hasCondition,
            hasLifetime,
            yearsRemaining
        };

        // Group all components by BaseType
        const baseType = component.type;
        if (grouped[baseType]) {
            grouped[baseType].push(componentPlan);
        }
    });

    // Sort each group alphabetically by specific type to maintain consistent position
    // This ensures components stay in place when condition/lifetime is added
    Object.keys(grouped).forEach(baseType => {
        grouped[baseType].sort((a, b) => {
            const aType = a.component.specificType?.value;
            const bType = b.component.specificType?.value;
            if (aType && bType) {
                return aType.localeCompare(bType);
            }
            return 0;
        });
    });

    return grouped;
};
